import { QrCode2, Search, TextSnippet } from '@mui/icons-material'
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import { Bin } from 'chezy-inventory-backend/bin/database/types/Bin'
import { Item } from 'chezy-inventory-backend/bin/database/types/Item'
import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ItemTable } from '../../components/itemTable'
import { getBaseURL } from '../../utils/getBaseURL'

import { BinResultRow } from './binResultRow'

export const ItemSearch = () => {
  const navigate = useNavigate()
  const [mode, setMode] = useState<string>('id')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [searchItemsFound, setSearchItemsFound] = useState<Item[]>([])
  const [searchBinsFound, setSearchBinsFound] = useState<Bin[]>([])
  const searchFieldRef = useRef<HTMLInputElement>()

  const onSearch = useCallback(() => {
    setSearchItemsFound([])
    setSearchBinsFound([])
    if (mode === 'id') {
      axios
        .get(`${getBaseURL()}/item/${searchQuery}`)
        .then(({ data }) => {
          setSearchItemsFound([data])
        })
        .catch(() => setSearchItemsFound([]))
    } else {
      axios
        .get(`${getBaseURL()}/items/search/${searchQuery}`)
        .then(({ data }) => {
          setSearchItemsFound(data)
        })
        .catch(() => setSearchItemsFound([]))
      axios
        .get(`${getBaseURL()}/bins/search/${searchQuery}`)
        .then(({ data }) => {
          setSearchBinsFound(data)
        })
        .catch(() => setSearchBinsFound([]))
    }
  }, [mode, searchQuery])

  return (
    <Box
      style={{ marginTop: 20 }}
      alignItems="flex-start"
      display="flex"
      flexDirection="row"
      gap="25px"
      width="100%"
      flexWrap="wrap"
    >
      <Box flexGrow={1} maxWidth="500px" minWidth="300px">
        <Box style={{ margin: 20 }}>
          <FormControl>
            <FormLabel id="demo-radio-buttons-group-label">Search By</FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-radio-buttons-group-label"
              value={mode}
              onChange={(ev) => {
                setMode(ev.target.value)
                searchFieldRef.current?.focus()
              }}
              name="radio-buttons-group"
            >
              <FormControlLabel value="id" control={<Radio />} label="Item ID" />
              <FormControlLabel value="text" control={<Radio />} label="Item &amp; Tote Metadata" />
            </RadioGroup>
          </FormControl>
        </Box>

        <Paper elevation={1} style={{ display: 'flex', padding: 20, gap: 20 }}>
          <TextField
            inputRef={searchFieldRef}
            style={{ flexGrow: 1 }}
            placeholder={mode === 'id' ? 'Scan item barcode' : 'Enter a search term'}
            value={searchQuery}
            onKeyUp={(ev) => {
              if (ev.key === 'Enter') {
                onSearch()
              }
            }}
            onChange={(ev) => {
              setSearchQuery(ev.target.value)
            }}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{mode === 'id' ? <QrCode2 /> : <TextSnippet />}</InputAdornment>
              ),
            }}
          />
          <Box style={{ marginTop: 8 }}>
            <IconButton
              onClick={() => {
                onSearch()
                searchFieldRef.current?.focus()
              }}
            >
              <Search />
            </IconButton>
          </Box>
        </Paper>
      </Box>
      <Box style={{ minWidth: 500 }} flexGrow={1} display="flex" flexDirection="column">
        {searchItemsFound.length === 0 && searchBinsFound.length === 0 && 'No Results'}
        {searchItemsFound.length > 0 && (
          <>
            <Typography variant="h5" marginBottom={2}>
              Found {searchItemsFound.length} item(s) matching query
            </Typography>
            <ItemTable items={searchItemsFound} showLocation />
          </>
        )}
        {searchBinsFound.length > 0 && (
          <>
            <Typography variant="h5" marginBottom={2} marginTop={4}>
              Found {searchBinsFound.length} bins(s) matching query
            </Typography>
            <Paper elevation={1} style={{ padding: '20px' }}>
              {searchBinsFound.map((bin) => (
                <BinResultRow bin={bin} onClick={() => navigate(`/bin/${bin.id}`)} />
              ))}
            </Paper>
          </>
        )}
      </Box>
    </Box>
  )
}
